// Title, description and link-preview tags of the page being shown.
//
// The build already writes them into each public page's own HTML
// (scripts/build-seo-pages.js). This keeps them right while the visitor moves
// around inside the app, where no new HTML is loaded. Same page list for
// both: seoPages.json.
import cfg from './seoPages.json';

const normalize = (pathname) => {
  const p = String(pathname || '/').split(/[?#]/)[0].replace(/\/+$/, '');
  return p === '' ? '/' : p;
};

export function findPage(pathname) {
  const path = normalize(pathname);
  return cfg.pages.find((p) => p.path === path || (p.aliases || []).includes(path)) || null;
}

// What the head must say at this address, or null when the page writes its
// own (public catalogues name themselves after the catalogue).
export function metaFor(pathname, lang) {
  const path = normalize(pathname);
  if (cfg.selfManaged.some((prefix) => `${path}/`.startsWith(prefix))) return null;
  const page = findPage(path);
  if (!page) return { ...cfg.default, canonical: null };
  const words = page.i18n?.[String(lang || 'es').slice(0, 2)] || page;
  return {
    title: words.title,
    description: words.description,
    canonical: `${cfg.site}${page.path}`,
  };
}

const upsert = (doc, selector, create, attr, value) => {
  let el = doc.head.querySelector(selector);
  if (value === null || value === undefined) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = create();
    doc.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const metaTag = (doc, key, name) => () => {
  const el = doc.createElement('meta');
  el.setAttribute(key, name);
  return el;
};

export function applyMeta(doc, meta) {
  if (!meta || !doc?.head) return;
  doc.title = meta.title;
  upsert(doc, 'meta[name="description"]', metaTag(doc, 'name', 'description'), 'content', meta.description);
  upsert(doc, 'link[rel="canonical"]', () => {
    const el = doc.createElement('link');
    el.setAttribute('rel', 'canonical');
    return el;
  }, 'href', meta.canonical);
  upsert(doc, 'meta[property="og:title"]', metaTag(doc, 'property', 'og:title'), 'content', meta.title);
  upsert(doc, 'meta[property="og:description"]', metaTag(doc, 'property', 'og:description'), 'content', meta.description);
  upsert(doc, 'meta[property="og:url"]', metaTag(doc, 'property', 'og:url'), 'content', meta.canonical);
  upsert(doc, 'meta[name="twitter:title"]', metaTag(doc, 'name', 'twitter:title'), 'content', meta.title);
  upsert(doc, 'meta[name="twitter:description"]', metaTag(doc, 'name', 'twitter:description'), 'content', meta.description);
}
